import React, { useState } from 'react';

import { Paper } from '@mui/material';
import BubbleChartRoundedIcon from '@mui/icons-material/BubbleChartRounded';

import {
    formatCompactInteger,
    formatInteger,
    formatPercentage
} from '../../utils/numberUtils';
import {
    buildMatrixData,
    MIN_RELEVANT_VISITS
} from './conversaoLPsData';

const chart = { width: 640, height: 360, left: 54, right: 18, top: 18, bottom: 42 };
const plotWidth = chart.width - chart.left - chart.right;
const plotHeight = chart.height - chart.top - chart.bottom;
const MAX_NAMED_POINTS = 8;
const MAX_LABEL_LENGTH = 18;
const AXIS_HEADROOM = 1.12;
const AXIS_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

const getAxisMax = (value) => {
    const target = value * AXIS_HEADROOM;
    const magnitude = 10 ** Math.floor(Math.log10(target));

    return AXIS_STEPS.find((step) => step * magnitude >= target) * magnitude;
};

const QUADRANTS = [
    { key: 'escalar', label: 'Escalar tráfego', hint: 'Converte bem, pouca visita', x: 'left', y: 'top' },
    { key: 'estrela', label: 'Estrelas', hint: 'Muita visita e boa conversão', x: 'right', y: 'top' },
    { key: 'rever', label: 'Rever', hint: 'Pouca visita e baixa conversão', x: 'left', y: 'bottom' },
    { key: 'otimizar', label: 'Otimizar página', hint: 'Muita visita, converte pouco', x: 'right', y: 'bottom' }
];

const getQuadrantKey = (point, medianVisitas, medianConversao) => {
    const isHighTraffic = point.visitas >= medianVisitas;
    const isHighConversion = point.conversao >= medianConversao;

    if (isHighConversion) return isHighTraffic ? 'estrela' : 'escalar';
    return isHighTraffic ? 'otimizar' : 'rever';
};

const truncateLabel = (label) => (
    label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…` : label
);

const ConversaoLPsMatrix = ({ stats }) => {
    const [activeId, setActiveId] = useState(null);
    const matrix = buildMatrixData(stats);

    if (!matrix) {
        return (
            <Paper className='crm-lp-panel crm-lp-matrix'>
                <div className='crm-lp-panel-heading'>
                    <div>
                        <p className='crm-dashboard-eyebrow'>Onde agir</p>
                        <h3>Tráfego × Conversão</h3>
                    </div>
                    <BubbleChartRoundedIcon />
                </div>
                <p className='crm-lp-empty'>{`A matriz precisa de pelo menos 2 LPs com ${MIN_RELEVANT_VISITS} visitas ou mais no período.`}</p>
            </Paper>
        );
    }

    const { medianConversao, medianVisitas, points } = matrix;
    const xMax = getAxisMax(Math.max(...points.map((point) => point.visitas)));
    const yMax = getAxisMax(Math.max(...points.map((point) => point.conversao), 1));
    const maxLeads = Math.max(...points.map((point) => point.leads), 1);
    const scaleX = (value) => chart.left + (value / xMax) * plotWidth;
    const scaleY = (value) => chart.top + plotHeight - (value / yMax) * plotHeight;
    const medianX = scaleX(medianVisitas);
    const medianY = scaleY(medianConversao);
    const showNames = points.length <= MAX_NAMED_POINTS;
    const plotted = points
        .map((point) => ({
            ...point,
            cx: scaleX(point.visitas),
            cy: scaleY(point.conversao),
            r: 6 + Math.sqrt(point.leads / maxLeads) * 14,
            quadrant: getQuadrantKey(point, medianVisitas, medianConversao)
        }))
        .sort((left, right) => right.r - left.r);
    const activePoint = plotted.find((point) => point.id === activeId);
    const ticks = [0, 0.5, 1];

    return (
        <Paper className='crm-lp-panel crm-lp-matrix'>
            <div className='crm-lp-panel-heading'>
                <div>
                    <p className='crm-dashboard-eyebrow'>Onde agir</p>
                    <h3>Tráfego × Conversão</h3>
                </div>
                <BubbleChartRoundedIcon />
            </div>

            <div className='crm-lp-matrix-chart' onMouseLeave={() => setActiveId(null)}>
                <svg viewBox={`0 0 ${chart.width} ${chart.height}`} role='img' aria-label='Matriz de visitas por conversão das landing pages'>
                    {QUADRANTS.map((quadrant) => {
                        const x = quadrant.x === 'left' ? chart.left : medianX;
                        const y = quadrant.y === 'top' ? chart.top : medianY;
                        const width = quadrant.x === 'left' ? medianX - chart.left : chart.left + plotWidth - medianX;
                        const height = quadrant.y === 'top' ? medianY - chart.top : chart.top + plotHeight - medianY;
                        const textX = quadrant.x === 'left' ? chart.left + 10 : chart.left + plotWidth - 10;
                        const textY = quadrant.y === 'top' ? chart.top + 18 : chart.top + plotHeight - 10;

                        return (
                            <g key={quadrant.key} className={`crm-lp-matrix-quadrant is-${quadrant.key}`}>
                                <rect x={x} y={y} width={Math.max(width, 0)} height={Math.max(height, 0)} />
                                <text x={textX} y={textY} textAnchor={quadrant.x === 'left' ? 'start' : 'end'}>
                                    {quadrant.label}
                                </text>
                            </g>
                        );
                    })}

                    {ticks.map((tick) => (
                        <g key={`y-${tick}`}>
                            <text className='crm-lp-matrix-axis-label' x={chart.left - 8} y={scaleY(yMax * tick) + 4} textAnchor='end'>
                                {`${formatPercentage(yMax * tick)}%`}
                            </text>
                            <text className='crm-lp-matrix-axis-label' x={scaleX(xMax * tick)} y={chart.top + plotHeight + 18} textAnchor='middle'>
                                {formatCompactInteger(xMax * tick)}
                            </text>
                        </g>
                    ))}

                    <line className='crm-lp-matrix-axis' x1={chart.left} x2={chart.left} y1={chart.top} y2={chart.top + plotHeight} />
                    <line className='crm-lp-matrix-axis' x1={chart.left} x2={chart.left + plotWidth} y1={chart.top + plotHeight} y2={chart.top + plotHeight} />
                    <line className='crm-lp-matrix-median' x1={medianX} x2={medianX} y1={chart.top} y2={chart.top + plotHeight} />
                    <line className='crm-lp-matrix-median' x1={chart.left} x2={chart.left + plotWidth} y1={medianY} y2={medianY} />

                    <text className='crm-lp-matrix-axis-title' x={chart.left + plotWidth / 2} y={chart.height - 4} textAnchor='middle'>
                        Visitas
                    </text>

                    {plotted.map((point) => (
                        <g
                            key={point.id}
                            className={`crm-lp-matrix-point is-${point.quadrant}${activeId === point.id ? ' is-active' : ''}`}
                            tabIndex='0'
                            aria-label={`${point.nome}: ${formatInteger(point.visitas)} visitas, ${formatInteger(point.leads)} leads, ${formatPercentage(point.conversao)}% de conversão`}
                            onBlur={() => setActiveId(null)}
                            onFocus={() => setActiveId(point.id)}
                            onMouseEnter={() => setActiveId(point.id)}
                        >
                            <circle cx={point.cx} cy={point.cy} r={point.r} />
                            {showNames && (
                                <text x={point.cx} y={point.cy - point.r - 6} textAnchor='middle'>
                                    {truncateLabel(point.nome)}
                                </text>
                            )}
                        </g>
                    ))}
                </svg>

                {activePoint && (
                    <div
                        className='crm-lp-tooltip'
                        style={{
                            left: `${(activePoint.cx / chart.width) * 100}%`,
                            top: `${((activePoint.cy - activePoint.r) / chart.height) * 100}%`
                        }}
                    >
                        <span>{activePoint.nome}</span>
                        <strong>{`${formatPercentage(activePoint.conversao)}% de conversão`}</strong>
                        <small>{`${formatInteger(activePoint.visitas)} visitas · ${formatInteger(activePoint.leads)} leads`}</small>
                        <small>{QUADRANTS.find((quadrant) => quadrant.key === activePoint.quadrant).hint}</small>
                    </div>
                )}
            </div>

            <p className='crm-lp-matrix-footnote'>
                {`Linhas tracejadas na mediana das LPs. Tamanho da bolha = leads. LPs com menos de ${MIN_RELEVANT_VISITS} visitas ficam de fora.`}
            </p>
        </Paper>
    );
};

export default ConversaoLPsMatrix;
